import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { Subject } from 'rxjs';
import { TablepageBaseComponent } from '../../abstract/tablepage-base/tablepage-base.component';
import { OrganizationDialogComponent } from '../../dialogs/organization-dialog/organization-dialog.component';
import { Organization } from '../../models/organization';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-organizations',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TablepageBaseComponent,
    OrganizationDialogComponent,
    TranslatePipe
  ],
  standalone: true,
  templateUrl: './organizations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class OrganizationsComponent implements OnInit, OnDestroy {
  messageService = inject(UiNotificationService);
  private translate = inject(TranslateService);

  organizations: Organization[] = [];
  selectedOrganization: Organization = Reset.organization();
  newOrganization: Organization = Reset.organization();

  loading: boolean = false;
  dtTrigger: Subject<boolean> = new Subject<boolean>();

  showDialog: boolean = false;
  isNew: boolean = false;
  constructor(private apiService: ApiService) {
  }

  ngOnInit(): void {
    this.loadData();
  }


  private loadData(): void {
    this.loading = true;

    this.apiService.getOrganizations().subscribe({
      next: (data) => {
        this.organizations = data;
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.messageService.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  ngOnDestroy() {
    this.dtTrigger.unsubscribe();
  }

  onOrganizationSaved(organization: Organization) {
    this.resetSelected();
    this.loadData();
  }

  onShowDialog(organization: Organization): void {
    this.isNew = organization.organizationId === 0;
    this.selectedOrganization = { ...organization }

    this.showDialog = true;
  }


  private resetSelected(): void {
    this.newOrganization = Reset.organization();
    this.selectedOrganization = Reset.organization();
  }

  exportToCSV(dt: Table, includeDepartments: boolean = false) {

    // Seleziono i dati filtrati -se è attivo un filtro, altrimenti tutti- e visibili (in tutte le pagine, non solo la prima)
    const filteredData = dt.filteredValue ?? dt.value;

    if (includeDepartments) {
      // Se voglio includere i dipartimenti, li carico dal server
      this.apiService.getDepartments().subscribe({
        next: (data) => {
          const departments = data;

          const deptMap = new Map<number, string[]>();
          departments.forEach(d => {
            if (!deptMap.has(d.organizationId)) deptMap.set(d.organizationId, []);
            deptMap.get(d.organizationId)?.push(d.name);
          });

          // Creo proprietà virtuale departmentNames
          const dataWithDepartments = filteredData.map(org => ({
            ...org,
            departmentNames: deptMap.get(org.organizationId)?.join(', ') ?? ''
          }));

          const columnDefinitions = [
            { field: 'organizationId', headerKey: 'ID' },
            { field: 'name', headerKey: 'name' },
            { field: 'departmentNames', headerKey: 'departments' },
            { field: 'isActive', headerKey: 'active' },
            { field: 'updatedAt', headerKey: 'lastUpdate' }
          ];

          const columns = columnDefinitions.map(col => ({
            field: col.field,
            header: this.translate.instant(col.headerKey)
          }));

          this.downloadCSV(dataWithDepartments, columns);


          this.dtTrigger.next(true);
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.messageService.showError(err);
        },
        complete: () => {
          this.loading = false;
        }
      })
    }
    else {
      // Se non vogliamo dipartimenti, generiamo subito il CSV
      const columnDefinitions = [
        { field: 'organizationId', headerKey: 'ID' },
        { field: 'name', headerKey: 'name' },
        { field: 'isActive', headerKey: 'active' },
        { field: 'updatedAt', headerKey: 'lastUpdate' }
      ];

      const columns = columnDefinitions.map(col => ({
        field: col.field,
        header: this.translate.instant(col.headerKey)
      }));

      this.downloadCSV(filteredData, columns);
    }
  }

  private downloadCSV(data: any[], columns: { field: string, header: string }[]) {
    const csvContent = this.convertToCSV(data, columns);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'organizations.csv';
    link.click();
  }

  private convertToCSV(data: any[], columns?: { field: string, header: string }[]): string {
    // Recupero i dati da esportare e creo il contenuto del file cella per cella in modo formattato
    if (!data || data.length === 0) return '';

    const separator = ';';

    // se voglio usare dei "nomi in chiaro" invece che le chiavi delle colonne:
    if (columns && columns.length > 0) {
      const headers = columns.map(c => c.header).join(separator);

      const csvRows = data.map(item =>
        columns.map(c => {
          const val = item[c.field] ?? '';
          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(separator)
      );

      return [headers, ...csvRows].join('\n');
    }

    // Altrimenti uso le chiavi (le recupero dalla prima riga di dati)
    const keys = Object.keys(data[0]);
    const header = keys.join(separator);

    const csvRows = data.map(item =>
      keys.map(k => {
        const val = item[k] ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(separator)
    );

    return [header, ...csvRows].join('\n');
  }
}
